import React, { useState } from "react";
import { toast } from "react-toastify";
import adminService from "../../api/services/adminService";
import axiosInstance from "../../api/services/axiosInstance";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUserCheck,
  FaPercentage,
} from "react-icons/fa";

const PromotionalMessage = () => {
  const [activeTab, setActiveTab] = useState("specific"); // "specific" or "all"
  const [searchEmail, setSearchEmail] = useState("");
  const [searchContactNo, setSearchContactNo] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5MB");
        return;
      }
      setSelectedImage(file);
      setError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        setError("Video size should be less than 50MB");
        return;
      }
      if (!file.type.startsWith("video/")) {
        setError("File must be a video");
        return;
      }
      setSelectedVideo(file);
      setError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => {
    setSelectedVideo(null);
    setVideoPreview(null);
  };

  const handleSearchUser = async () => {
    if (!searchEmail && !searchContactNo) {
      setError("Please enter email or contact number");
      return;
    }

    setSearching(true);
    setError("");
    setSearchedUser(null);
    try {
      const response = await adminService.searchUserForPromotionalMessage(
        searchEmail || null,
        searchContactNo || null
      );
      if (response.success) {
        setSearchedUser(response.user);
        setSuccessMsg("User found successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(response.message || "User not found");
        setSearchedUser(null);
      }
    } catch (error) {
      setError(error?.message || "Error searching user");
      setSearchedUser(null);
    } finally {
      setSearching(false);
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) return null;

    const formData = new FormData();
    formData.append("file", selectedImage);

    try {
      const response = await axiosInstance.post(
        "admin/users/upload/image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.imageUrl;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || "Failed to upload image"
      );
    }
  };

  const uploadVideo = async () => {
    if (!selectedVideo) return null;

    try {
      const response = await adminService.uploadPromotionalVideo(selectedVideo);
      return response.videoUrl;
    } catch (error) {
      throw new Error(error?.message || "Failed to upload video");
    }
  };

  const handleSendToSpecificUser = async () => {
    if (!searchedUser) {
      setError("Please search and select a user first");
      return;
    }

    // Validate that at least one content type is provided
    if (!message && !selectedImage && !selectedVideo && !url) {
      setError(
        "Please enter a message, select an image/video, or provide a URL"
      );
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      let imageUrl = null;
      let videoUrl = null;

      if (selectedImage) {
        imageUrl = await uploadImage();
      }

      if (selectedVideo) {
        videoUrl = await uploadVideo();
      }

      const payload = {
        email: searchEmail || null,
        contactNo: searchContactNo || null,
        message: message || null,
        image: imageUrl,
        video: videoUrl,
        url: url || null,
      };

      const response = await adminService.sendPromotionalMessageToUser(payload);
      if (response.success) {
        setSuccessMsg("Promotional message sent successfully!");
        toast.success("Promotional message sent successfully!");
        // Reset form
        setMessage("");
        setSelectedImage(null);
        setImagePreview(null);
        setSelectedVideo(null);
        setVideoPreview(null);
        setUrl("");
        setSearchedUser(null);
        setSearchEmail("");
        setSearchContactNo("");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(response.message || "Failed to send message");
      }
    } catch (error) {
      setError(error?.message || "Error sending promotional message");
    } finally {
      setLoading(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // User Info Card Component (matching MatchUser page style exactly)
  const UserInfoCard = ({ user }) => {
    if (!user) return null;

    const getStatusBadge = (status, label) => {
      return (
        <span
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
            status ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-600"
          }`}>
          {status ? (
            <FaCheckCircle className="text-xs" />
          ) : (
            <FaTimesCircle className="text-xs" />
          )}
          {label}
        </span>
      );
    };

    return (
      <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-red-200 shadow-lg overflow-hidden">
        <div className="p-4 sm:p-5 md:p-6">
          {/* User Avatar & Basic Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 mb-4 sm:mb-5 md:mb-6 pb-4 sm:pb-5 md:pb-6 border-b border-gray-200">
            <div className="flex-shrink-0">
              {user.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={user.firstName}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 sm:border-4 border-red-300 shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white flex items-center justify-center text-red-500 text-2xl sm:text-3xl font-bold shadow-lg border-2 sm:border-4 border-red-500">
                  {user.firstName?.[0]?.toUpperCase() || "?"}
                  {user.lastName?.[0]?.toUpperCase() || ""}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 break-words">
                {user.firstName || ""} {user.lastName || ""}
              </h4>
              <p className="text-xs sm:text-sm text-gray-500 break-all">
                {user.email}
              </p>
              {user.tagline && (
                <p className="text-xs sm:text-sm text-red-600 font-medium mt-1 italic break-words">
                  "{user.tagline}"
                </p>
              )}
              {user.percentage && (
                <div className="mt-2">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <FaPercentage className="text-red-500 text-xs sm:text-sm" />
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">
                      Profile Completion: {user.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="h-2 rounded-full bg-red-500"
                      style={{ width: `${user.percentage}%` }}></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-5 md:mb-6 justify-center sm:justify-start">
            {getStatusBadge(user.isActive === 1, "Active")}
            {getStatusBadge(user.isProfileComplete === 1, "Profile Complete")}
            {getStatusBadge(user.isVerified === 1, "Verified")}
            {getStatusBadge(user.isBlur === 0, "Profile Visible")}
            {getStatusBadge(user.isDisable === 0, "Not Disabled")}
            {user.isAdmin === 1 && (
              <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                <FaUserCheck className="text-xs" /> Admin
              </span>
            )}
          </div>

          {/* User Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
            {/* Personal Information */}
            <div className="space-y-2 sm:space-y-3">
              <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
                Personal Information
              </h5>
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <FaIdCard className="text-[#FF2D38] w-4" />
                  <span className="text-gray-600 font-medium">User ID:</span>
                  <span className="text-gray-800 font-semibold">
                    #{user.id}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaEnvelope className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">Email:</span>
                  </div>
                  <span className="text-gray-800 break-all">{user.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaPhone className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">Phone:</span>
                  </div>
                  <span className="text-gray-800">
                    {user.contactNo || "N/A"}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaUser className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">Gender:</span>
                  </div>
                  <span className="text-gray-800">{user.gender || "N/A"}</span>
                </div>
                {user.age && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Age:</span>
                    <span className="text-gray-800">{user.age} years</span>
                  </div>
                )}
                {user.height && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Height:</span>
                    <span className="text-gray-800">{user.height} cm</span>
                  </div>
                )}
                {user.maritalStatus && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Marital Status:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.maritalStatus}
                    </span>
                  </div>
                )}
                {user.religion && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Religion:</span>
                    <span className="text-gray-800 break-words">
                      {user.religion}
                    </span>
                  </div>
                )}
                {user.sect && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Sect:</span>
                    <span className="text-gray-800 break-words">
                      {user.sect}
                    </span>
                  </div>
                )}
                {user.diet && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Diet:</span>
                    <span className="text-gray-800 break-words">
                      {user.diet}
                    </span>
                  </div>
                )}
                {user.smoking && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Smoking:</span>
                    <span className="text-gray-800 break-words">
                      {user.smoking}
                    </span>
                  </div>
                )}
                {user.alcohol && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Alcohol:</span>
                    <span className="text-gray-800 break-words">
                      {user.alcohol}
                    </span>
                  </div>
                )}
                {user.languages && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Languages:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.languages}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Professional & Location Information */}
            <div className="space-y-2 sm:space-y-3">
              <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
                Professional & Location
              </h5>
              <div className="space-y-1.5 sm:space-y-2">
                {user.occupation && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Occupation:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.occupation}
                    </span>
                  </div>
                )}
                {user.education && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Education:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.education}
                    </span>
                  </div>
                )}
                {user.careerStatus && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Career Status:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.careerStatus}
                    </span>
                  </div>
                )}
                {user.monthlyIncome && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Monthly Income:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.monthlyIncome}
                    </span>
                  </div>
                )}
                {user.address && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <FaMapMarkerAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                      <span className="text-gray-600 font-medium">
                        Address:
                      </span>
                    </div>
                    <span className="text-gray-800 break-words">
                      {user.address}
                    </span>
                  </div>
                )}
                {user.grewUp && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Grew Up:</span>
                    <span className="text-gray-800 break-words">
                      {user.grewUp}
                    </span>
                  </div>
                )}
                {user.residential && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Residential:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.residential}
                    </span>
                  </div>
                )}
                {user.marriage && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Marriage Plan:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.marriage}
                    </span>
                  </div>
                )}
                {user.parenting && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Parenting Goal:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.parenting}
                    </span>
                  </div>
                )}
                {user.childerns !== undefined && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Children:</span>
                    <span className="text-gray-800">
                      {user.childerns === 0
                        ? "No children"
                        : `${user.childerns} children`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* About Me & Hobbies */}
          {(user.aboutMe || user.hobbiesNames) && (
            <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
              {user.aboutMe && (
                <div className="mb-3 sm:mb-4">
                  <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2">
                    About Me
                  </h5>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed bg-gray-50 p-2 sm:p-3 rounded-lg border border-red-100 break-words">
                    {user.aboutMe}
                  </p>
                </div>
              )}
              {user.hobbiesNames && (
                <div>
                  <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2">
                    Hobbies & Interests
                  </h5>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {user.hobbiesNames.split(",").map((hobby, idx) => (
                      <span
                        key={idx}
                        className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200">
                        {hobby.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account Status */}
          <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
            <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3">
              Account Details
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              {user.createdDate && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaCalendarAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">
                      Created:
                    </span>
                  </div>
                  <span className="text-gray-800 text-xs sm:text-sm">
                    {formatDate(user.createdDate)}
                  </span>
                </div>
              )}
              {user.modifiedDate && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaCalendarAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">
                      Modified:
                    </span>
                  </div>
                  <span className="text-gray-800 text-xs sm:text-sm">
                    {formatDate(user.modifiedDate)}
                  </span>
                </div>
              )}
              {user.latitude && user.longitude && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaMapMarkerAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">
                      Location:
                    </span>
                  </div>
                  <span className="text-gray-800 text-xs break-all">
                    {user.latitude}, {user.longitude}
                  </span>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-600 font-medium text-xs sm:text-sm">
                  Login Type:
                </span>
                <div className="flex flex-wrap gap-1">
                  {user.isGoogle === 1 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      Google
                    </span>
                  )}
                  {user.isApple === 1 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                      Apple
                    </span>
                  )}
                  {user.isGoogle === 0 && user.isApple === 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      Email
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSendToAllUsers = async () => {
    // Validate that at least one content type is provided
    if (!message && !selectedImage && !selectedVideo && !url) {
      setError(
        "Please enter a message, select an image/video, or provide a URL"
      );
      return;
    }

    const confirmSend = window.confirm(
      "Are you sure you want to send this promotional message to ALL users? This action cannot be undone."
    );

    if (!confirmSend) return;

    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      let imageUrl = null;
      let videoUrl = null;

      if (selectedImage) {
        imageUrl = await uploadImage();
      }

      if (selectedVideo) {
        videoUrl = await uploadVideo();
      }

      const payload = {
        message: message || null,
        image: imageUrl,
        video: videoUrl,
        url: url || null,
      };

      const response = await adminService.sendPromotionalMessageToAll(payload);
      if (response.success) {
        const successMessage = `Promotional messages sent: ${
          response.sentCount
        } successful, ${response.failedCount || 0} failed`;
        setSuccessMsg(successMessage);
        toast.success(successMessage);
        // Reset form
        setMessage("");
        setSelectedImage(null);
        setImagePreview(null);
        setSelectedVideo(null);
        setVideoPreview(null);
        setUrl("");
        setTimeout(() => setSuccessMsg(""), 5000);
      } else {
        setError(response.message || "Failed to send messages");
      }
    } catch (error) {
      setError(error?.message || "Error sending promotional messages");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg md:text-2xl font-bold text-gray-800">
          Promotional Messages
        </h2>
        {successMsg && (
          <div className="text-green-700 bg-green-100 px-4 py-2 rounded-lg text-sm font-medium border border-green-200">
            {successMsg}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4 bg-white rounded-xl shadow-md p-1 inline-flex">
        <button
          onClick={() => {
            setActiveTab("specific");
            setError("");
            setSuccessMsg("");
          }}
          className={`px-6 py-2 rounded-lg font-medium text-xs md:text-sm transition-all ${
            activeTab === "specific"
              ? "bg-red-500 text-white shadow-md"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
          }`}>
          Send to Specific User
        </button>
        <button
          onClick={() => {
            setActiveTab("all");
            setError("");
            setSuccessMsg("");
          }}
          className={`px-6 py-2 rounded-lg font-medium text-xs md:text-sm transition-all ${
            activeTab === "all"
              ? "bg-red-500 text-white shadow-md"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
          }`}>
          Send to All Users
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <div className="font-semibold">Error:</div>
              <div>{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-center">
          <div className="flex items-center justify-center gap-3">
            <svg
              className="animate-spin h-5 w-5 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="font-medium">
              {activeTab === "specific"
                ? "Sending message..."
                : "Sending to all users..."}
            </span>
          </div>
        </div>
      )}

      {/* Specific User Tab */}
      {activeTab === "specific" && (
        <div className="bg-white p-2 md:p-6 md:rounded-xl md:shadow-xl">
          <h3 className="text-md md:text-xl font-bold text-gray-800 mb-6">
            Send Promotional Message to Specific User
          </h3>

          {/* Search User Section */}
          <div className="mb-6">
            <h4 className="text-md  md:text-lg font-bold mb-4 text-gray-800">
              Search User
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="searchEmail"
                  className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  id="searchEmail"
                  type="email"
                  value={searchEmail}
                  onChange={(e) => {
                    setSearchEmail(e.target.value);
                    setError("");
                  }}
                  className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="searchContactNo"
                  className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Number
                </label>
                <input
                  id="searchContactNo"
                  type="text"
                  value={searchContactNo}
                  onChange={(e) => {
                    setSearchContactNo(e.target.value);
                    setError("");
                  }}
                  className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                  placeholder="+1234567890"
                />
              </div>
            </div>
            <button
              onClick={handleSearchUser}
              disabled={searching}
              className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm md:text-md font-semibold shadow-md transition-colors">
              {searching ? "Searching..." : "Search User"}
            </button>

            {/* Searched User Info - Full Details Card */}
            {searchedUser && (
              <div className="mt-6">
                <UserInfoCard user={searchedUser} />
              </div>
            )}
          </div>

          {/* Message Section */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-md md:text-lg font-bold mb-4 text-gray-800">
              Message Content
            </h4>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="message"
                  className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    setError("");
                  }}
                  rows={6}
                  className="w-full text-xs md:text-sm p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                  placeholder="Enter your promotional message here..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  At least one content type (message, image, video, or URL) is
                  required
                </p>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                  Image (Optional)
                </label>
                {!selectedImage ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true">
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="mt-4 flex text-xs md:text-sm leading-6 text-gray-600 justify-center">
                        <span className="relative cursor-pointer rounded-md  font-semibold text-red-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2 hover:text-red-500">
                          Upload an image
                        </span>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs leading-5 text-gray-600 mt-2">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </label>
                  </div>
                ) : (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-w-full md:max-w-md h-auto rounded-lg border border-gray-300"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-600 text-xs md:text-sm text-white px-3 py-1.5 rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors">
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Video Upload */}
              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                  Video (Optional)
                </label>
                {!selectedVideo ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoChange}
                      className="hidden"
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="mt-4 flex text-xs md:text-sm leading-6 text-gray-600 justify-center">
                        <span className="relative cursor-pointer rounded-md font-semibold text-red-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2 hover:text-red-500">
                          Upload a video
                        </span>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs leading-5 text-gray-600 mt-2">
                        MP4, MOV, AVI up to 50MB
                      </p>
                    </label>
                  </div>
                ) : (
                  <div className="relative inline-block">
                    <video
                      src={videoPreview}
                      controls
                      className="max-w-full text-xs md:text-sm md:max-w-md h-auto rounded-lg border border-gray-300">
                      Your browser does not support the video tag.
                    </video>
                    <button
                      onClick={handleRemoveVideo}
                      className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors">
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* URL/Link Input */}
              <div>
                <label
                  htmlFor="url"
                  className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                  URL / Link (Optional)
                </label>
                <input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setError("");
                  }}
                  className="w-full text-xs md:text-sm p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                  placeholder="https://example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter a URL to share with the message
                </p>
              </div>
            </div>
          </div>

          {/* Send Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleSendToSpecificUser}
              disabled={
                loading ||
                !searchedUser ||
                (!message && !selectedImage && !selectedVideo && !url)
              }
              className="w-full md:w-auto px-8 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs md:text-sm font-semibold shadow-md transition-colors">
              {loading ? "Sending..." : "Send Message"}
            </button>
          </div>
        </div>
      )}

      {/* All Users Tab */}
      {activeTab === "all" && (
        <div className="bg-white p-6 rounded-xl shadow-xl">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            Send Promotional Message to All Users
          </h3>

          {/* Warning Banner */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <div className="font-semibold text-yellow-800">Warning</div>
                <div className="text-sm text-yellow-700 mt-1">
                  This will send the promotional message to all active users in
                  the system. This action cannot be undone.
                </div>
              </div>
            </div>
          </div>

          {/* Message Section */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="messageAll"
                className="block text-sm font-semibold text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                id="messageAll"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setError("");
                }}
                rows={6}
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                placeholder="Enter your promotional message here..."
              />
              <p className="text-xs text-gray-500 mt-1">
                At least one content type (message, image, video, or URL) is
                required
              </p>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Image (Optional)
              </label>
              {!selectedImage ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload-all"
                  />
                  <label htmlFor="image-upload-all" className="cursor-pointer">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true">
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-4 flex text-sm leading-6 text-gray-600">
                      <span className="relative cursor-pointer rounded-md font-semibold text-red-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2 hover:text-red-500">
                        Upload an image
                      </span>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs leading-5 text-gray-600 mt-2">
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </label>
                </div>
              ) : (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-full md:max-w-md h-auto rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors">
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Video Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Video (Optional)
              </label>
              {!selectedVideo ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    className="hidden"
                    id="video-upload-all"
                  />
                  <label htmlFor="video-upload-all" className="cursor-pointer">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <div className="mt-4 flex text-sm leading-6 text-gray-600">
                      <span className="relative cursor-pointer rounded-md font-semibold text-red-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2 hover:text-red-500">
                        Upload a video
                      </span>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs leading-5 text-gray-600 mt-2">
                      MP4, MOV, AVI up to 50MB
                    </p>
                  </label>
                </div>
              ) : (
                <div className="relative inline-block">
                  <video
                    src={videoPreview}
                    controls
                    className="max-w-full md:max-w-md h-auto rounded-lg border border-gray-300">
                    Your browser does not support the video tag.
                  </video>
                  <button
                    onClick={handleRemoveVideo}
                    className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors">
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* URL/Link Input */}
            <div>
              <label
                htmlFor="urlAll"
                className="block text-sm font-semibold text-gray-700 mb-2">
                URL / Link (Optional)
              </label>
              <input
                id="urlAll"
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError("");
                }}
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                placeholder="https://example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter a URL to share with the message
              </p>
            </div>
          </div>

          {/* Send Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleSendToAllUsers}
              disabled={
                loading ||
                (!message && !selectedImage && !selectedVideo && !url)
              }
              className="w-full md:w-auto px-8 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold shadow-md transition-colors">
              {loading ? "Sending to All Users..." : "Send to All Users"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionalMessage;
